import React, { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { COUNTRIES } from "@/lib/countries";
import { flagEmoji } from "@/lib/format";
import { cn } from "@/lib/utils";

// Searchable single-country selector (full ISO list w/ flags). `worldwide` adds 🌐 Worldwide (WW)
// on top of the list — used everywhere an offer GEO is picked.
export function CountrySelect({ label, value, onChange, placeholder = "Select country...", allowClear, worldwide = true, testId, triggerClass }) {
  const [open, setOpen] = useState(false);
  const sel = value === "WW" ? { code: "WW", name: "Worldwide" } : COUNTRIES.find((c) => c.code === value);
  return (
    <div className={cn(!label && "contents")}>
      {label && <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</Label>}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testId} role="combobox"
            className={cn("flex h-10 w-full items-center justify-between rounded-md border border-white/[0.1] bg-[#0A0A0A] px-3 text-sm text-foreground outline-none transition-colors hover:border-white/20 focus:border-white/25", triggerClass)}>
            <span className={cn("flex items-center gap-2 truncate", !sel && "text-muted-foreground")}>
              {sel ? <><span className="text-base leading-none">{flagEmoji(sel.code)}</span> {sel.name}</> : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] border-white/[0.1] bg-[#1a1a1e] p-0" align="start">
          <Command className="bg-transparent">
            <CommandInput placeholder="Search country..." className="text-sm" />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {allowClear && (
                  <CommandItem value="__all__" onSelect={() => { onChange(""); setOpen(false); }} className="text-muted-foreground">
                    All countries
                  </CommandItem>
                )}
                {worldwide && (
                  <CommandItem value="Worldwide WW" data-testid={testId ? `${testId}-opt-WW` : undefined}
                    onSelect={() => { onChange("WW"); setOpen(false); }}>
                    <span className="mr-2 text-base leading-none">🌐</span>
                    <span className="flex-1">Worldwide</span>
                    <span className="mr-1 text-xs text-muted-foreground">WW</span>
                    {value === "WW" && <Check className="h-4 w-4" />}
                  </CommandItem>
                )}
                {COUNTRIES.map((c) => (
                  <CommandItem key={c.code} value={`${c.name} ${c.code}`} data-testid={testId ? `${testId}-opt-${c.code}` : undefined}
                    onSelect={() => { onChange(c.code); setOpen(false); }}>
                    <span className="mr-2 text-base leading-none">{flagEmoji(c.code)}</span>
                    <span className="flex-1">{c.name}</span>
                    <span className="mr-1 text-xs text-muted-foreground">{c.code}</span>
                    {value === c.code && <Check className="h-4 w-4" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
